import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getCurrentFY, getFinancialYearMonthsPassed } from "../../../Utilitie";

const monthsRef = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const IREController = ({
  data,
  setIREFilteredData,
  openStates: propOpenStates,
  setOpenStates,
  setActiveMetrics,
}) => {
  // Fallback for openStates if not provided or incomplete
  const [internalOpenStates, setInternalOpenStates] = useState({
    "Financial Year": false,
    Month: false,
    Metrics: false,
  });

  // Use propOpenStates if provided, otherwise use internalOpenStates
  const openStates = propOpenStates || internalOpenStates;

  // State for search inputs (one per dropdown)
  const [searchValues, setSearchValues] = useState({
    "Financial Year": "",
    Month: "",
    Metrics: "",
  });
  // State for selections
  const [selectedFY, setSelectedFY] = useState(new Set());
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectedMetrics, setSelectedMetrics] = useState(
    new Set(["JUMBO"]), // Default to a single metric
  );

  useEffect(() => {
    setActiveMetrics(selectedMetrics.values().next().value);
  }, [selectedMetrics]);

  // Modified toggleSelection to handle single selection for Metrics
  const toggleSelection = (setFunc, currentSet, value, isMetrics = false) => {
    if (isMetrics) {
      // For Metrics, replace the current selection with the new value
      setFunc(new Set([value]));
    } else {
      // For other dropdowns, keep multi-selection behavior
      const newSet = new Set(currentSet);
      newSet.has(value) ? newSet.delete(value) : newSet.add(value);
      setFunc(newSet);
    }
  };

  const toggleDropdown = (title) => {
    const updateFunc = setOpenStates || setInternalOpenStates;
    updateFunc((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Define all possible sub-metrics for each metric group
  const metricGroups = useMemo(
    () => ({
      JUMBO: [
        "JUMBO_RECD_tot",
        "JUMBO_IRE_tot",
        "JUMBO_Y_DAY_O_B_tot",
        "JUMBO_PERCENTILE_tot",
      ],
      BTPN: [
        "BTPN_RECD_tot",
        "BTPN_IRE_tot",
        "BTPN_Y_DAY_O_B_tot",
        "BTPN_PERCENTILE_tot",
      ],
      NBOX: [
        "NBOX_RECD_tot",
        "NBOX_IRE_tot",
        "NBOX_Y_DAY_O_B_tot",
        "NBOX_PERCENTILE_tot",
      ],
    }),
    [],
  );

  // Filtered data based on selections
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data
      .filter((entry) => selectedFY.has(entry.financial_year))
      .map((entry) => {
        const filteredMonths = (entry.children || [])
          .filter((month) => selectedMonths.has(month.monthName))
          .map((month) => {
            const filteredData = (month.data || [])
              .map((d) => {
                const filteredEntry = {};
                // Include all sub-metrics for the selected metric group
                selectedMetrics.forEach((metric) => {
                  const subMetrics = metricGroups[metric] || [];
                  subMetrics.forEach((subMetric) => {
                    if (d.hasOwnProperty(subMetric)) {
                      filteredEntry[subMetric] = d[subMetric];
                    }
                  });
                });
                return filteredEntry;
              })
              .filter((d) => Object.keys(d).length > 0);
            return { ...month, data: filteredData };
          })
          .filter((month) => month.data.length > 0);
        return { ...entry, children: filteredMonths };
      })
      .filter((entry) => entry.children.length > 0);
  }, [data, selectedFY, selectedMonths, selectedMetrics, metricGroups]);

  // Available options for dropdowns
  const availableFY = useMemo(() => {
    return [...new Set((data || []).map((d) => d.financial_year))];
  }, [data]);
  const availableMonths = useMemo(() => {
    const months = [
      ...new Set(
        (data || [])
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .map((month) => month.monthName),
      ),
    ].sort((a, b) => {
      const indexA = monthsRef.findIndex((ref) =>
        a.toLowerCase().startsWith(ref.toLowerCase()),
      );
      const indexB = monthsRef.findIndex((ref) =>
        b.toLowerCase().startsWith(ref.toLowerCase()),
      );
      return indexA - indexB;
    });
    return months;
  }, [data, selectedFY]);

  const availableMetrics = useMemo(() => ["JUMBO", "NBOX", "BTPN"], []);

  // Default setup for initial state
  const defaultSetup = () => {
    if (!data?.length) return;

    const allFY = new Set(data.map((d) => d.financial_year));
    const allMonths = new Set(
      data.flatMap((d) => d.children || []).map((month) => month.monthName),
    );
    const defaultMetric = new Set(["JUMBO"]); // Default to single metric

    const monthsPassed = getFinancialYearMonthsPassed(getCurrentFY(), true);
    setSelectedFY(allFY);
    setSelectedMonths(monthsPassed);
    setSelectedMetrics(defaultMetric);

    const updateFunc = setOpenStates || setInternalOpenStates;
    updateFunc({
      "Financial Year": false,
      Month: false,
      Metrics: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Update filtered data
  useEffect(() => {
    if (typeof setIREFilteredData === "function") {
      setIREFilteredData(filteredData);
    } else {
      console.warn(
        "setIREFilteredData is not a function. Using internal state for filtered data.",
      );
    }
  }, [filteredData, setIREFilteredData]);

  // Render checkbox or radio list for dropdowns
  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const isMetrics = title === "Metrics";
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        .toLowerCase()
        .includes(searchValues[title].toLowerCase()),
    );

    return (
      <div className="col-md mt-2 p-1">
        <div
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="newUiBody"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title}{" "}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="newUiBody"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title]}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sm-4 fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type={isMetrics ? "radio" : "checkbox"}
                          name={isMetrics ? "metrics-radio" : undefined}
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(
                              setFunc,
                              selectedSet,
                              item,
                              isMetrics,
                            )
                          }
                        />
                        {title === "Month" ? item.slice(0, 3) : item}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              {!isMetrics && (
                <div className="d-flex justify-content-evenly mt-1">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setFunc(new Set(items))}
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => setFunc(new Set())}
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ zIndex: 9 }}>
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "Financial Year",
          availableFY,
          selectedFY,
          setSelectedFY,
        )}
        {renderCheckboxList(
          "Month",
          availableMonths,
          selectedMonths,
          setSelectedMonths,
        )}
        {renderCheckboxList(
          "Metrics",
          availableMetrics,
          selectedMetrics,
          setSelectedMetrics,
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-1">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 1px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default IREController;
